import { RefreshCw } from 'lucide-react';
import { useConversions } from '@/hooks/queries/useConversions';
import { useI18n } from '@/hooks/useI18n';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { ErrorInline } from '@/components/primitives/ErrorInline';
import { formatInt, formatRelative } from '@/lib/format';
import { cn } from '@/lib/cn';
import type { ConversionsResponse } from '@/types/api';
import { ConversionCharts } from './ConversionCharts';
import { ConversionAccountsTable } from './ConversionAccountsTable';

export function GetMySocialSection({
  periodDays,
  identityId,
  identityName,
}: {
  periodDays: number;
  identityId?: string;
  identityName?: string;
}) {
  const { t } = useI18n();
  const query = useConversions(periodDays, identityId);
  const data = query.data;
  return (
    <section
      id="analytics-conversions"
      aria-labelledby="analytics-conversions-title"
      className="min-w-0 scroll-mt-20 space-y-4"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h2
            id="analytics-conversions-title"
            className="text-[18px] font-semibold tracking-tight text-text-1"
          >
            {t('Clics et conversions GetMySocial', 'GetMySocial clicks and conversions')}
          </h2>
          <p className="mt-1 text-[12px] leading-5 text-text-3">
            {t(
              'Objectif : plus de clics vers vos destinations.',
              'Goal: more clicks to your destinations.',
            )}{' '}
            · {identityName ?? t('Toutes les identités', 'All identities')}
          </p>
        </div>
        <Button
          variant="ghost"
          size="icon"
          disabled={query.isFetching}
          aria-label={t('Actualiser GetMySocial', 'Refresh GetMySocial')}
          onClick={() => void query.refetch()}
        >
          <RefreshCw
            className={cn('size-4', query.isFetching && 'animate-spin')}
            aria-hidden="true"
          />
        </Button>
      </div>
      {query.isError ? (
        <ErrorInline error={query.error} onRetry={() => void query.refetch()} />
      ) : query.isLoading || !data ? (
        <div role="status" aria-label={t('Chargement des conversions', 'Loading conversions')}>
          <Skeleton className="h-40 w-full rounded-[14px]" />
        </div>
      ) : data.status === 'LOADING' ? (
        <div
          role="status"
          className="flex items-center gap-3 rounded-[12px] border border-line-1 bg-bg-1 p-4 text-[13px] leading-6 text-text-3"
        >
          <RefreshCw className="size-4 shrink-0 animate-spin" aria-hidden="true" />
          <p>
            {t('Synchronisation des liens GetMySocial en cours…', 'Syncing GetMySocial links…')}
          </p>
        </div>
      ) : data.status === 'NOT_CONFIGURED' ? (
        <p className="rounded-2xl border border-line-1 bg-bg-1 p-5 text-[13px] leading-6 text-text-3">
          {t(
            'La connexion GetMySocial est temporairement indisponible. Réessayez plus tard.',
            'The GetMySocial connection is temporarily unavailable. Try again later.',
          )}
        </p>
      ) : (
        <ConversionContent data={data} />
      )}
    </section>
  );
}

function ConversionContent({ data }: { data: ConversionsResponse }) {
  const { t, locale } = useI18n();
  const summary = data.summary;
  const decimal = (value: number | null) =>
    value === null ? '—' : value.toLocaleString(locale, { maximumFractionDigits: 2 });
  const warningLabels: Record<string, string> = {
    DATA_UNAVAILABLE: t(
      'Certaines données GetMySocial sont indisponibles. Réessayez plus tard.',
      'Some GetMySocial data is unavailable. Try again later.',
    ),
    SHARED_LINKS: t(
      'Des liens sont partagés entre plusieurs comptes. Leurs clics sont comptés une seule fois dans le total et ne sont pas attribués à un compte.',
      'Some links are shared between accounts. Their clicks count once in the total and are not attributed to an account.',
    ),
    LINK_HISTORY_INCOMPLETE: t(
      'L’historique des liens est incomplet ou a changé : seuls les liens stables sur toute la période participent aux comparaisons.',
      'Link history is incomplete or has changed: only links stable across the full period contribute to comparisons.',
    ),
    PARTIAL_COVERAGE: t(
      'La couverture est partielle : les clics ne représentent que les liens rapprochés disponibles.',
      'Coverage is partial: clicks represent only available matched links.',
    ),
    DIRECT_LINKS: t(
      'Les liens directs ne mesurent pas de clic sur un bouton. Leurs ouvertures ne sont pas comptées comme clics vers la destination.',
      'Direct links do not measure a button click. Their opens are not counted as destination clicks.',
    ),
  };
  const warnings = [...new Set(data.warnings.map((code) => warningLabels[code]).filter(Boolean))];
  const date = (value: string) =>
    new Date(value).toLocaleDateString(locale, {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      timeZone: 'UTC',
    });
  const lastCompletedDay = new Date(new Date(data.to).getTime() - 86_400_000).toISOString();
  return (
    <div className="space-y-4">
      <p className="text-[12px] font-medium text-text-2">
        {t(
          `Journées UTC terminées · jusqu’au ${date(lastCompletedDay)}`,
          `Completed UTC days · through ${date(lastCompletedDay)}`,
        )}
      </p>
      {(warnings.length > 0 || data.status === 'UNAVAILABLE' || data.status === 'PARTIAL') && (
        <div
          className="space-y-1 rounded-[12px] border border-amber/25 bg-amber-soft px-4 py-3 text-[12px] leading-5 text-text-2"
          role="status"
        >
          <p className="font-medium">
            {data.status === 'UNAVAILABLE'
              ? t('Données temporairement indisponibles', 'Data temporarily unavailable')
              : t('Périmètre partiellement mesuré', 'Partially measured scope')}
          </p>
          {warnings.map((warning) => (
            <p key={warning}>{warning}</p>
          ))}
        </div>
      )}
      <div className="grid min-w-0 grid-cols-2 gap-3 xl:grid-cols-4">
        <ConversionMetric
          label={t('Clics vers la destination', 'Destination clicks')}
          value={formatInt(summary.clicks)}
          context={t('Visites ayant cliqué sur un bouton', 'Visits that clicked a button')}
          emphasis
        />
        <ConversionMetric
          label={t('Comptes avec lien actif', 'Accounts with an active link')}
          value={`${formatInt(summary.activeLinkAccounts)} / ${formatInt(summary.totalAccounts)}`}
          context={t(
            'Dernier lien bio observé et actif dans GetMySocial',
            'Latest observed bio link, active in GetMySocial',
          )}
        />
        <ConversionMetric
          label={t('Clics pour 1 000 vues', 'Clicks per 1,000 views')}
          value={decimal(summary.clicksPerThousandViews)}
          context={t(
            `${formatInt(summary.comparableAccounts)} comptes comparables`,
            `${formatInt(summary.comparableAccounts)} comparable accounts`,
          )}
        />
        <ConversionMetric
          label={t('Conversion de la page', 'Landing page conversion')}
          value={
            summary.landingConversionPercent === null
              ? '—'
              : `${decimal(summary.landingConversionPercent)} %`
          }
          context={t(
            `${formatInt(summary.pageviews)} visites GetMySocial · pages avec boutons`,
            `${formatInt(summary.pageviews)} GetMySocial visits · pages with buttons`,
          )}
        />
      </div>
      <div className="flex flex-wrap gap-x-5 gap-y-1 text-[11px] leading-5 text-text-3">
        <span>
          {t(
            `${formatInt(summary.matchedAccounts)} comptes rapprochés`,
            `${formatInt(summary.matchedAccounts)} matched accounts`,
          )}
        </span>
        <span>
          {t(
            `${formatInt(summary.sharedLinkAccounts)} avec lien partagé`,
            `${formatInt(summary.sharedLinkAccounts)} with a shared link`,
          )}
        </span>
        <span>
          {t(
            `${formatInt(summary.unmatchedAccounts)} non rapprochés`,
            `${formatInt(summary.unmatchedAccounts)} unmatched`,
          )}
        </span>
        <span>
          {date(data.from)} – {date(lastCompletedDay)} · UTC
        </span>
      </div>
      <ConversionCharts data={data} />
      <ConversionAccountsTable accounts={data.accounts} />
      <details className="rounded-[12px] border border-dashed border-line-2 px-4 text-[12px] text-text-3">
        <summary className="min-h-11 cursor-pointer py-3 font-medium text-text-2">
          {t('Périmètre, attribution et méthode', 'Scope, attribution and methodology')}
        </summary>
        <div className="space-y-2 pb-4 leading-5">
          <p>
            {t(
              'Les clics destination correspondent aux visites GetMySocial ayant cliqué sur au moins un bouton, pas à la somme des clics de chaque bouton. Le total déduplique les liens rapprochés, y compris les liens partagés. Les clics historiques des liens retirés ou remplacés restent inclus si GetMySocial les conserve. La conversion de page divise ces visites par les visites des pages avec boutons.',
              'Destination clicks are GetMySocial visits that clicked at least one button, not the sum of individual button clicks. The total deduplicates matched links, including shared links. Historical clicks from removed or replaced links remain included when GetMySocial retains them. Landing conversion divides those visits by visits to pages with buttons.',
            )}
          </p>
          <p>
            {t(
              'Les conversions utilisent les 7, 30 ou 90 dernières journées UTC terminées, contrairement aux fenêtres glissantes des autres analyses. L’historique complet commence à la première journée complète après le premier profil Instagram observé. L’évolution globale des clics conserve tous les liens rapprochés uniques. La comparaison clics/vues, les ratios et les corrélations utilisent uniquement les comptes avec un lien individuel stable depuis le début de la période et au moins un relevé de vues exploitable chaque jour. Un lien partagé à un moment de la période est exclu de ces comparaisons. Les gains de vues des Reels sont affectés au jour du relevé. Le ratio global divise les clics comparables par les vues comparables, puis multiplie par 1 000 ; ce n’est pas un taux de clic attribué aux Reels.',
              'Conversions use the last 7, 30 or 90 completed UTC days, unlike the rolling windows in other analytics. All time starts on the first complete day after the first observed Instagram profile. The overall click trend includes all unique matched links. Click/view comparisons, ratios and correlations use only accounts with an individual link stable since the beginning of the period and at least one usable view observation every day. A link shared at any time in the period is excluded from these comparisons. Reel view gains are assigned to the observation day. The overall ratio divides comparable clicks by comparable views, then multiplies by 1,000; it is not a click-through rate attributed to Reels.',
            )}
          </p>
          <p>
            {t(
              'Un lien actif associe le dernier lien bio observé et un lien actif dans GetMySocial. Cet état reflète la dernière collecte du profil, pas une vérification Instagram en direct. Un tiret indique une donnée manquante, une attribution ambiguë ou un ratio impossible ; un zéro est une mesure réelle.',
              'An active link combines the latest observed bio link and an active GetMySocial link. This reflects the latest profile collection, not a live Instagram check. A dash means missing data, ambiguous attribution or an undefined ratio; zero is an actual measurement.',
            )}
          </p>
        </div>
      </details>
      <p className="text-[11px] leading-5 text-text-3">
        {t('Dernière récupération GetMySocial', 'Latest GetMySocial fetch')} :{' '}
        {data.fetchedAt ? formatRelative(data.fetchedAt) : '—'} ·{' '}
        {t(
          'Cache serveur : 5 minutes. Actualiser recharge les analyses sans forcer une nouvelle collecte.',
          'Server cache: 5 minutes. Refresh reloads analytics without forcing a new collection.',
        )}
      </p>
    </div>
  );
}

function ConversionMetric({
  label,
  value,
  context,
  emphasis = false,
}: {
  label: string;
  value: string;
  context: string;
  emphasis?: boolean;
}) {
  return (
    <article
      aria-label={label}
      className={cn(
        'min-w-0 rounded-[12px] border border-line-1 bg-bg-1 p-3 sm:p-4',
        emphasis && 'border-emerald/20 bg-emerald/[0.04]',
      )}
    >
      <h3 className="text-[11px] font-medium leading-4 text-text-3">{label}</h3>
      <p
        className={cn(
          'my-2 break-words text-[25px] font-semibold tabular-nums tracking-tight',
          emphasis ? 'text-emerald' : 'text-text-1',
        )}
      >
        {value}
      </p>
      <p className="text-[10px] leading-4 text-text-3">{context}</p>
    </article>
  );
}
